import { AttachmentType } from "@/modules/beneficiary/common/enum";
import type {
  IOpenAttachmentDialogProps,
  TBeneficiaryAttachmentItem
} from "@/modules/beneficiary/common/types";
import { createBeneficiaryValidationSchema } from "@/modules/beneficiary/validations";
import { FormContainer } from "@ab.uitools/ui-library/components/FormContainer";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { AttachModal } from "./AttachModal";
import { CreateBeneficiaryForm } from "./CreateBeneficiaryForm";

export const CreateBeneficiary = () => {
  const { t } = useTranslation();
  const [attachmentType, setAttachmentType] = useState<AttachmentType>(AttachmentType.Account);
  const [formAttachmentType, setFormAttachmentType] = useState<AttachmentType | null>(AttachmentType.Account);
  const [isOpenAttachmentForm, setIsOpenAttachmentForm] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [accounts, setAccounts] = useState<TBeneficiaryAttachmentItem[]>([]);
  const [cards, setCards] = useState<TBeneficiaryAttachmentItem[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>();
  const [selectedItem, setSelectedItem] = useState<TBeneficiaryAttachmentItem | null>(null);
  const [clientId, setClientId] = useState<string>("");
  const [allowClientChange, setAllowClientChange] = useState<boolean>(false);
  const initialValues = {
    name: "",
    cards: [],
    accounts: [],
    isAvailableForOrgUsers: false,
  };

  const onOpenAttachModal = (data?: IOpenAttachmentDialogProps) => {
    if (data?.item && data?.type) {
      setFormAttachmentType(data.type);
      setSelectedItem(data.item);
      setSelectedIndex(data.index);
      setIsEdit(true);
      const isAccount = data.type === AttachmentType.Account;
      const otherItems = [
        ...(isAccount ? accounts.filter((_, i) => i !== data.index) : accounts),
        ...(isAccount ? cards : cards.filter((_, i) => i !== data.index)),
      ];
      setAllowClientChange(!otherItems.some(item => item.clientId));
    } else {
      setFormAttachmentType(attachmentType);
      setAllowClientChange(false);
    }
    setIsOpenAttachmentForm(true);
  };

  const onDeleteAttached = (type: AttachmentType, index: number) => {
    const isAccount = type === AttachmentType.Account;
    const newList = (isAccount ? accounts : cards).filter((_, i) => i !== index);
    const remaining = [...newList, ...(isAccount ? cards : accounts)];
    setClientId(remaining.find(item => item.clientId)?.clientId ?? "");
    (isAccount ? setAccounts : setCards)(newList);
  };

  const onClose = () => {
    setIsEdit(false);
    setSelectedItem(null);
    setFormAttachmentType(null);
    setIsOpenAttachmentForm(false);
    setSelectedIndex(null);
    setAllowClientChange(false);
  };

  const onAttach = (data: TBeneficiaryAttachmentItem) => {
    const isAccount = formAttachmentType === AttachmentType.Account;
    const attachedList = isAccount ? accounts : cards;
    const setAttachedList = isAccount ? setAccounts : setCards;
    if (isEdit) {
      const newList = attachedList.map((item, i) => i === selectedIndex ? data : item);
      setAttachedList(newList);
      const allItems = isAccount ? [...newList, ...cards] : [...accounts, ...newList];
      setClientId(allItems.find(item => item.clientId)?.clientId ?? "");
    } else {
      setAttachedList((prev) => [...prev, data]);
    }
    onClose();
  };

  return (
    <>
      <FormContainer
        className="full-width page-container form-layout beneficiary-form"
        formId="form-container"
        mode="onBlur"
        reValidateMode="onBlur"
        initialValues={initialValues}
        validationScheme={createBeneficiaryValidationSchema({ t })}
      >
        <CreateBeneficiaryForm
          setAttachmentType={setAttachmentType}
          accounts={accounts}
          cards={cards}
          attachmentType={attachmentType}
          onOpenAttachModal={onOpenAttachModal}
          onDeleteAttached={onDeleteAttached}
        />
      </FormContainer>
      <AttachModal
        data={selectedItem}
        isOpen={isOpenAttachmentForm}
        onClose={onClose}
        isEdit={isEdit}
        cards={cards}
        accounts={accounts}
        onConfirm={onAttach}
        clientId={clientId}
        setClientId={setClientId}
        attachmentType={formAttachmentType}
        allowClientChange={allowClientChange}
      />
    </>
  );
};
